import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Button from "../../components/Button";
import Checkbox from "../../components/Checkbox";
import { colors } from "../../config/colors";
import { images, svg } from "../../config/images";
import TextInput from "../../components/TextInput";
import {
  EMAIL,
  ONLY_ALPHABETS,
  PASSWORD_VERY_STRONG,
  testRegex,
} from "../../config/regex";
import { useAuth } from "../../context/AuthContext";
import { requestRegisterOtp, verifyRegisterOtp } from "../../api";

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [step, setStep] = useState("form"); // 'form' | 'otp'
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const navigate = useNavigate();
  const { registerUser } = useAuth();

  React.useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const validateField = (field, value) => {
    const v = String(value ?? "").trim();
    switch (field) {
      case "firstName":
        if (!v) return "First name is required";
        if (!testRegex(ONLY_ALPHABETS, v)) return "Only alphabets allowed";
        return "";
      case "lastName":
        if (!v) return "Last name is required";
        if (!testRegex(ONLY_ALPHABETS, v)) return "Only alphabets allowed";
        return "";
      case "email":
        if (!v) return "Email address is required";
        if (!testRegex(EMAIL, v)) return "Enter a valid email address";
        return "";
      case "password":
        if (!v) return "Password is required";
        if (v.length < 8)
          return "Password must be 8 chars with 1 letter & 1 number & 1 special char";
        if (!testRegex(PASSWORD_VERY_STRONG, v))
          return "Must include uppercase, lowercase, number, and special character";
        return "";
      case "confirmPassword":
        if (!v) return "Confirmation is required";
        if (v !== formData.password) return "Passwords do not match";
        return "";
      default:
        return "";
    }
  };

  const normalizeName = (value) =>
    String(value ?? "").replace(/[^A-Za-z]/g, "");

  const setAndValidate = (field, value) => {
    const finalValue =
      field === "firstName" || field === "lastName"
        ? normalizeName(value)
        : value;

    setFormData((prev) => ({ ...prev, [field]: finalValue }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, finalValue),
    }));
    setError("");
    setOtpError("");
  };

  const handleChange = (e) => {
    setAndValidate(e.target.name, e.target.value);
  };

  const performRegister = async () => {
    const result = await registerUser(
      formData.firstName.trim(),
      formData.lastName.trim(),
      formData.email.trim(),
      formData.password,
      "",
    );
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === "form") {
      const nextErrors = {
        firstName: validateField("firstName", formData.firstName),
        lastName: validateField("lastName", formData.lastName),
        email: validateField("email", formData.email),
        password: validateField("password", formData.password),
        confirmPassword: validateField(
          "confirmPassword",
          formData.confirmPassword,
        ),
      };

      Object.keys(nextErrors).forEach((key) => {
        if (!nextErrors[key]) delete nextErrors[key];
      });

      setFieldErrors(nextErrors);

      if (Object.keys(nextErrors).length) {
        setError("Please fix the highlighted fields.");
        return;
      }

      if (!agreeTerms) {
        setError("You must agree to the Terms & Conditions and Privacy Policy");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await requestRegisterOtp(formData.email.trim());
        if (!res.success) {
          setError(res.message || "Failed to send OTP");
        } else {
          setStep("otp");
          setTimeLeft(60);
        }
      } catch (_) {
        setError("Failed to send OTP. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // step === 'otp'
    if (!otp.trim()) {
      setOtpError("Please enter the OTP sent to your email");
      return;
    }
    if (!/^\d{4,6}$/.test(otp.trim())) {
      setOtpError("Enter a valid OTP");
      return;
    }

    setLoading(true);
    setOtpError("");
    setError("");
    try {
      const verifyRes = await verifyRegisterOtp(
        formData.email.trim(),
        otp.trim(),
      );
      if (!verifyRes.success) {
        setOtpError(verifyRes.message || "OTP verification failed");
        return;
      }

      const result = await performRegister();
      if (result.success) {
        navigate("/home");
      } else {
        setError(result.message || "Registration failed");
      }
    } catch (_) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || timeLeft > 0) return;
    setLoading(true);
    setError("");
    setOtpError("");
    try {
      const res = await requestRegisterOtp(formData.email.trim());
      if (!res.success) {
        setError(res.message || "Failed to resend OTP");
      } else {
        setTimeLeft(60);
        setOtp("");
      }
    } catch (_) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12 lg:py-5 4xl:py-24">
        <div className="w-full max-w-6xl 4xl:max-w-11xl grid md:grid-cols-2 gap-8 md:gap-12 4xl:gap-32 items-center">
          {/* Left Side - Branding & Illustration */}
          <div className="hidden md:flex flex-col items-center justify-center relative">
            <div className="relative w-full max-w-lg 4xl:max-w-3xl">
              {/* Logo Section */}
              <div className="flex items-center gap-4 4xl:gap-8 mb-8 4xl:mb-16">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-16 4xl:h-28 w-auto"
                />
                <div>
                  <h1 className="text-4xl whitespace-nowrap 4xl:text-7xl font-bold text-primary-600">
                    Market Mavericks
                  </h1>
                  <p className="text-gray-600 text-sm 4xl:text-2xl">
                    Start Your Investment Journey Today
                  </p>
                </div>
              </div>

              {/* Animated Chart Illustration */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 4xl:p-16 border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-secondary-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary-200 rounded-full -ml-12 -mb-12 opacity-20"></div>

                <div className="relative z-10">
                  <h3 className="text-2xl 4xl:text-5xl font-bold text-gray-800 mb-2">
                    Join the Trading Community
                  </h3>
                  <p className="text-gray-600 4xl:text-2xl mb-6">
                    Learn, trade, and grow with expert guidance
                  </p>

                  {/* Chart SVG */}
                  <div className="bg-gradient-to-br from-secondary-50 to-primary-50 rounded-xl p-6 4xl:p-12 mb-6 4xl:mb-12">
                    <svg viewBox="0 0 400 200" className="w-full h-40 4xl:h-72">
                      {/* Grid lines */}
                      <line
                        x1="0"
                        y1="40"
                        x2="400"
                        y2="40"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="80"
                        x2="400"
                        y2="80"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="120"
                        x2="400"
                        y2="120"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />
                      <line
                        x1="0"
                        y1="160"
                        x2="400"
                        y2="160"
                        stroke={colors.gray[200]}
                        strokeWidth="1"
                      />

                      {/* Chart line - growth trend */}
                      <polyline
                        points="20,160 60,150 100,130 140,110 180,90 220,70 260,55 300,45 340,38 380,32"
                        fill="none"
                        stroke={colors.secondary[500]}
                        strokeWidth="3"
                        className="animate-dash"
                      />

                      {/* Data points */}
                      {[20, 60, 100, 140, 180, 220, 260, 300, 340, 380].map(
                        (x, i) => {
                          const y = 160 - (i * 12 + Math.random() * 5);
                          return (
                            <circle
                              key={i}
                              cx={x}
                              cy={y}
                              r="4"
                              fill={colors.secondary[500]}
                              className="animate-pulse"
                              style={{ animationDelay: `${i * 0.1}s` }}
                            />
                          );
                        },
                      )}
                    </svg>
                  </div>

                  {/* Benefits */}
                  <div className="space-y-3 4xl:space-y-6">
                    <div className="flex items-center gap-3 4xl:gap-6">
                      <div className="w-8 h-8 4xl:w-14 4xl:h-14 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <img
                          src={svg.checkmarkThick}
                          alt="check"
                          className="w-4 h-4 4xl:w-7 4xl:h-7"
                          style={{
                            filter:
                              "brightness(0) saturate(100%) invert(28%) sepia(95%) saturate(1234%) hue-rotate(100deg) brightness(94%) contrast(88%)",
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-sm 4xl:text-xl font-semibold text-gray-800">
                          Expert-Led Courses
                        </div>
                        <div className="text-xs 4xl:text-lg text-gray-600">
                          Learn from industry professionals
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 4xl:gap-6">
                      <div className="w-8 h-8 4xl:w-14 4xl:h-14 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <img
                          src={svg.checkmarkThick}
                          alt="check"
                          className="w-4 h-4 4xl:w-7 4xl:h-7"
                          style={{
                            filter:
                              "brightness(0) saturate(100%) invert(65%) sepia(95%) saturate(1234%) hue-rotate(100deg) brightness(94%) contrast(88%)",
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-sm 4xl:text-xl font-semibold text-gray-800">
                          Live Market Analysis
                        </div>
                        <div className="text-xs 4xl:text-lg text-gray-600">
                          Real-time insights and strategies
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 4xl:gap-6">
                      <div className="w-8 h-8 4xl:w-14 4xl:h-14 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <img
                          src={svg.checkmarkThick}
                          alt="check"
                          className="w-4 h-4 4xl:w-7 4xl:h-7"
                          style={{
                            filter:
                              "brightness(0) saturate(100%) invert(28%) sepia(95%) saturate(1234%) hue-rotate(100deg) brightness(94%) contrast(88%)",
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-sm 4xl:text-xl font-semibold text-gray-800">
                          Community Support
                        </div>
                        <div className="text-xs 4xl:text-lg text-gray-600">
                          Connect with fellow traders
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Register Form */}
          <div className="w-full max-w-xl 4xl:max-w-3xl mx-auto md:mx-0">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 lg:p-10 4xl:p-20 border border-gray-100">
              {/* Mobile Logo */}
              <div className="md:hidden flex items-center gap-3 mb-6">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-12 w-auto"
                />
                <h2 className="text-2xl font-bold text-primary-600">
                  Market Mavericks
                </h2>
              </div>

              <div className="mb-8 4xl:mb-16">
                <h2 className="text-3xl 4xl:text-6xl font-bold text-gray-900 mb-2">
                  Create Your Account
                </h2>
                <p className="text-gray-600 4xl:text-2xl">
                  Join thousands of successful traders today
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                noValidate
                className="space-y-4 4xl:space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 4xl:gap-8">
                  <TextInput
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    error={fieldErrors.firstName}
                    required
                  />
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    error={fieldErrors.lastName}
                    required
                  />
                </div>

                <TextInput
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  error={fieldErrors.email}
                  disabled={step === "otp"}
                  required
                />

                <TextInput
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  error={fieldErrors.password}
                  required
                  icon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="text-gray-400 hover:text-primary-500 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  iconPosition="right"
                />

                <TextInput
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  error={fieldErrors.confirmPassword}
                  required
                  icon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="text-gray-400 hover:text-primary-500 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  }
                  iconPosition="right"
                />

                <div className="flex items-start gap-3">
                  <Checkbox
                    name="agreeTerms"
                    required
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                  />

                  <span className="text-sm 4xl:text-xl">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 font-sm"
                    >
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 font-sm"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </div>

                {step === "form" && (
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full !py-3"
                    disabled={loading}
                  >
                    {loading ? "Sending OTP..." : "Send OTP"}
                  </Button>
                )}

                {step === "otp" && (
                  <div className="space-y-4 4xl:space-y-8 pt-2">
                    <TextInput
                      label="Enter OTP"
                      name="otp"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setOtpError("");
                      }}
                      placeholder="6-digit code sent to your email"
                      error={otpError}
                      required
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full !py-3"
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify OTP"}
                    </Button>

                    <div className="text-center">
                      {timeLeft > 0 ? (
                        <p className="text-sm 4xl:text-xl text-gray-500">
                          Resend OTP in{" "}
                          <span className="font-semibold text-primary-600">
                            {timeLeft}s
                          </span>
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={loading}
                          className="text-sm 4xl:text-xl font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </form>

              <p className="mt-6 4xl:mt-12 text-center text-gray-600 4xl:text-2xl">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Sign in here
                </Link>
              </p>

              <div className="mt-4 4xl:mt-8 text-center">
                <Link
                  to="/"
                  className="text-sm 4xl:text-xl text-gray-500 hover:text-gray-700"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
